/**
 * Mock Data Generator for TerraMind
 * Generates realistic agricultural data for demonstration
 * Data is generated dynamically based on current date
 */

// Generate random number within range
const randomInRange = (min, max, decimals = 2) => {
    const value = Math.random() * (max - min) + min
    return Number(value.toFixed(decimals))
}

// Format date to YYYY-MM-DD
const formatDate = (date) => date.toISOString().split('T')[0]

// Get date N days ago
const daysAgo = (days) => {
    const date = new Date()
    date.setDate(date.getDate() - days)
    return date
}

// Crop optimal ranges and pricing
export const CROP_CONFIG = {
    'Arroz': {
        emoji: '🌾',
        ranges: {
            ph: { min: 5.5, max: 6.5 },
            humidity: { min: 60, max: 80 },
            nitrogen: { min: 40, max: 80 },
            phosphorus: { min: 20, max: 40 },
            potassium: { min: 120, max: 200 },
            temperature: { min: 20, max: 35 }
        },
        finance: {
            pricePerKg: 2800,
            yieldTonHa: 4.5,
            productionCostPerHa: 8500000
        }
    },
    'Arándanos': {
        emoji: '🫐',
        ranges: {
            ph: { min: 4.5, max: 5.5 },
            humidity: { min: 40, max: 60 },
            nitrogen: { min: 20, max: 40 },
            phosphorus: { min: 15, max: 30 },
            potassium: { min: 80, max: 150 },
            temperature: { min: 15, max: 25 }
        },
        finance: {
            pricePerKg: 18000,
            yieldTonHa: 8,
            productionCostPerHa: 45000000
        }
    },
    'Maíz': {
        emoji: '🌽',
        ranges: {
            ph: { min: 5.8, max: 7.0 },
            humidity: { min: 50, max: 70 },
            nitrogen: { min: 50, max: 100 },
            phosphorus: { min: 20, max: 50 },
            potassium: { min: 100, max: 200 },
            temperature: { min: 18, max: 32 }
        },
        finance: {
            pricePerKg: 1200,
            yieldTonHa: 9,
            productionCostPerHa: 6000000
        }
    },
    'Tomate': {
        emoji: '🍅',
        ranges: {
            ph: { min: 6.0, max: 6.8 },
            humidity: { min: 45, max: 65 },
            nitrogen: { min: 30, max: 60 },
            phosphorus: { min: 25, max: 45 },
            potassium: { min: 150, max: 250 },
            temperature: { min: 18, max: 27 }
        },
        finance: {
            pricePerKg: 3500,
            yieldTonHa: 100,
            productionCostPerHa: 85000000
        }
    },
    'Gulupa': {
        emoji: '🍇',
        ranges: {
            ph: { min: 5.5, max: 6.5 },
            humidity: { min: 50, max: 70 },
            nitrogen: { min: 25, max: 50 },
            phosphorus: { min: 20, max: 40 },
            potassium: { min: 100, max: 180 },
            temperature: { min: 15, max: 25 }
        },
        finance: {
            pricePerKg: 12000,
            yieldTonHa: 25,
            productionCostPerHa: 35000000
        }
    }
}

// Legacy compatibility
export const CROP_RANGES = Object.fromEntries(
    Object.entries(CROP_CONFIG).map(([k, v]) => [k, v.ranges])
)

// Zone names
const ZONE_NAMES = ['Zona Norte', 'Zona Sur', 'Zona Este', 'Zona Oeste']

// ========================================
// SOIL DATA
// ========================================
export function generateSoilData(crop, zones = 4) {
    const config = CROP_CONFIG[crop] || CROP_CONFIG['Tomate']
    const ranges = config.ranges

    return ZONE_NAMES.slice(0, zones).map((zoneName, index) => {
        const isProblematic = index === 0

        return {
            id: index + 1,
            name: zoneName,
            nitrogen: isProblematic
                ? randomInRange(ranges.nitrogen.min - 15, ranges.nitrogen.min)
                : randomInRange(ranges.nitrogen.min, ranges.nitrogen.max),
            phosphorus: randomInRange(ranges.phosphorus.min, ranges.phosphorus.max),
            potassium: randomInRange(ranges.potassium.min, ranges.potassium.max),
            ph: randomInRange(ranges.ph.min, ranges.ph.max, 1),
            humidity: randomInRange(ranges.humidity.min, ranges.humidity.max, 1),
            conductivity: randomInRange(0.5, 2.0),
            organicMatter: randomInRange(2.5, 5.5, 1),
            soilTemperature: randomInRange(18, 26, 1),
            lastUpdated: new Date().toISOString()
        }
    })
}

// ========================================
// HISTORICAL DATA (Last 30 days from today)
// ========================================
export function generateHistoricalData(crop, days = 30) {
    const config = CROP_CONFIG[crop] || CROP_CONFIG['Tomate']
    const ranges = config.ranges
    const data = []

    for (let i = days; i >= 0; i--) {
        const date = daysAgo(i)

        // Add some daily variation
        const dayVariation = Math.sin(i * 0.2) * 0.1

        data.push({
            date: formatDate(date),
            dateDisplay: date.toLocaleDateString('es-CO', { day: '2-digit', month: 'short' }),
            ph: randomInRange(ranges.ph.min * (1 + dayVariation), ranges.ph.max * (1 - dayVariation), 1),
            humidity: randomInRange(ranges.humidity.min, ranges.humidity.max, 0),
            nitrogen: randomInRange(ranges.nitrogen.min * 0.8, ranges.nitrogen.max * 1.1, 0),
            phosphorus: randomInRange(ranges.phosphorus.min, ranges.phosphorus.max, 0),
            potassium: randomInRange(ranges.potassium.min, ranges.potassium.max, 0),
            temperature: randomInRange(ranges.temperature.min, ranges.temperature.max, 1)
        })
    }

    return data
}

// ========================================
// SOIL DATA BY DATE (Last 30 days, per zone, per day)
// ========================================
export function generateSoilHistoryByZone(crop, days = 30) {
    const config = CROP_CONFIG[crop] || CROP_CONFIG['Tomate']
    const ranges = config.ranges
    const data = {}

    ZONE_NAMES.forEach((zoneName, zoneIndex) => {
        data[zoneName] = []
        const isProblematic = zoneIndex === 0

        for (let i = days; i >= 0; i--) {
            const date = daysAgo(i)
            const variation = Math.sin(i * 0.15 + zoneIndex) * 0.1

            data[zoneName].push({
                date: formatDate(date),
                dateDisplay: date.toLocaleDateString('es-CO', { day: '2-digit', month: 'short' }),
                nitrogen: isProblematic && i < 7
                    ? randomInRange(ranges.nitrogen.min - 20, ranges.nitrogen.min - 5)
                    : randomInRange(ranges.nitrogen.min, ranges.nitrogen.max) * (1 + variation),
                phosphorus: randomInRange(ranges.phosphorus.min, ranges.phosphorus.max),
                potassium: randomInRange(ranges.potassium.min, ranges.potassium.max),
                ph: randomInRange(ranges.ph.min, ranges.ph.max, 1),
                humidity: randomInRange(ranges.humidity.min, ranges.humidity.max, 1),
                conductivity: randomInRange(0.5, 2.0),
                organicMatter: randomInRange(2.5, 5.5, 1)
            })
        }
    })

    return data
}

// ========================================
// ENVIRONMENT DATA
// ========================================
export function generateEnvironmentData(crop) {
    const config = CROP_CONFIG[crop] || CROP_CONFIG['Tomate']
    const ranges = config.ranges

    return {
        current: {
            airTemperature: randomInRange(ranges.temperature.min, ranges.temperature.max, 1),
            humidity: randomInRange(55, 75, 0),
            solarRadiation: randomInRange(400, 800, 0),
            precipitation: randomInRange(0, 5, 1),
            windSpeed: randomInRange(5, 20, 1),
            windDirection: randomInRange(0, 360, 0),
            evapotranspiration: randomInRange(2, 6, 1)
        },
        forecast: generateForecast(7, ranges.temperature),
        gdd: {
            accumulated: randomInRange(800, 1500, 0),
            target: 2000,
            dailyAvg: randomInRange(10, 25, 1),
            daysToHarvest: Math.floor(randomInRange(30, 90, 0))
        }
    }
}

// ========================================
// ENVIRONMENT HISTORY (Last 30 days)
// ========================================
export function generateEnvironmentHistory(crop, days = 30) {
    const config = CROP_CONFIG[crop] || CROP_CONFIG['Tomate']
    const ranges = config.ranges
    const data = []

    for (let i = days; i >= 0; i--) {
        const date = daysAgo(i)
        const isRainy = Math.random() > 0.7

        data.push({
            date: formatDate(date),
            dateDisplay: date.toLocaleDateString('es-CO', { day: '2-digit', month: 'short' }),
            tempMax: randomInRange(ranges.temperature.max - 3, ranges.temperature.max + 2, 1),
            tempMin: randomInRange(ranges.temperature.min - 2, ranges.temperature.min + 3, 1),
            tempAvg: randomInRange(ranges.temperature.min + 2, ranges.temperature.max - 2, 1),
            humidity: randomInRange(50, 85, 0),
            precipitation: isRainy ? randomInRange(5, 25, 1) : 0,
            radiation: isRainy ? randomInRange(200, 400, 0) : randomInRange(500, 800, 0),
            windSpeed: randomInRange(5, 25, 1),
            gddDaily: randomInRange(8, 20, 1)
        })
    }

    return data
}

// Generate weather forecast
function generateForecast(days, tempRange) {
    const forecast = []
    const conditions = ['☀️', '⛅', '🌤️', '🌧️', '⛈️']

    for (let i = 0; i < days; i++) {
        const date = new Date()
        date.setDate(date.getDate() + i)

        forecast.push({
            date: formatDate(date),
            dayName: date.toLocaleDateString('es-CO', { weekday: 'short' }),
            condition: conditions[Math.floor(Math.random() * conditions.length)],
            tempMax: randomInRange(tempRange.max - 2, tempRange.max + 3, 0),
            tempMin: randomInRange(tempRange.min - 2, tempRange.min + 3, 0),
            rain: Math.random() > 0.6 ? randomInRange(1, 15, 0) : 0,
            humidity: randomInRange(50, 80, 0)
        })
    }

    return forecast
}

// ========================================
// ALERTS
// ========================================
export function generateAlerts(crop) {
    const config = CROP_CONFIG[crop] || CROP_CONFIG['Tomate']
    const ranges = config.ranges

    return [
        {
            id: 1,
            type: 'warning',
            severity: 'alta',
            title: 'Nitrógeno bajo en Zona Norte',
            description: `Valor actual: ${Math.round(ranges.nitrogen.min - 10)} mg/kg. Rango óptimo: ${ranges.nitrogen.min}-${ranges.nitrogen.max} mg/kg`,
            recommendation: 'Aplicar fertilizante nitrogenado (urea o nitrato de amonio). Dosis: 150-200 kg/ha',
            timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
            zone: 'Zona Norte'
        },
        {
            id: 2,
            type: 'info',
            severity: 'media',
            title: 'Pronóstico de lluvia',
            description: 'Se esperan precipitaciones mañana (8-12mm)',
            recommendation: 'Considere posponer aplicación de pesticidas y preparar drenajes',
            timestamp: new Date(Date.now() - 4 * 60 * 60 * 1000).toISOString(),
            zone: 'General'
        },
        {
            id: 3,
            type: 'success',
            severity: 'baja',
            title: 'pH en rango óptimo',
            description: `Todas las zonas dentro del rango ${ranges.ph.min}-${ranges.ph.max}`,
            recommendation: 'Mantener programa de fertilización actual',
            timestamp: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
            zone: 'Todas'
        }
    ]
}

// ========================================
// PRODUCTIVITY DATA
// ========================================
export function generateProductivityData(crop, days = 30) {
    const config = CROP_CONFIG[crop] || CROP_CONFIG['Tomate']
    const dailyYield = (config.finance.yieldTonHa * 1000) / 365 // kg per day approx

    const harvests = []
    const labors = []

    // Generate harvests (not every day, varies)
    for (let i = days; i >= 0; i--) {
        const date = daysAgo(i)
        const hasHarvest = Math.random() > 0.6

        if (hasHarvest) {
            const qualityRandom = Math.random()
            let quality = 'Primera'
            if (qualityRandom > 0.85) quality = 'Premium'
            else if (qualityRandom > 0.6) quality = 'Primera'
            else if (qualityRandom > 0.3) quality = 'Segunda'
            else quality = 'Tercera'

            harvests.push({
                id: harvests.length + 1,
                date: formatDate(date),
                dateDisplay: date.toLocaleDateString('es-CO', { day: '2-digit', month: 'short' }),
                quantity: randomInRange(dailyYield * 0.5, dailyYield * 2, 1),
                quality,
                zone: ZONE_NAMES[Math.floor(Math.random() * ZONE_NAMES.length)],
                brix: crop === 'Tomate' ? randomInRange(4, 8, 1) : null
            })
        }
    }

    // Generate labors
    const laborTypes = ['Riego', 'Fertilización', 'Fumigación', 'Poda', 'Cosecha', 'Preparación']
    for (let i = 0; i < 15; i++) {
        const date = daysAgo(Math.floor(Math.random() * days))
        labors.push({
            id: i + 1,
            date: formatDate(date),
            dateDisplay: date.toLocaleDateString('es-CO', { day: '2-digit', month: 'short' }),
            type: laborTypes[Math.floor(Math.random() * laborTypes.length)],
            zone: ZONE_NAMES[Math.floor(Math.random() * ZONE_NAMES.length)],
            workers: Math.floor(randomInRange(2, 8, 0)),
            hours: randomInRange(4, 10, 1),
            notes: ''
        })
    }

    labors.sort((a, b) => new Date(b.date) - new Date(a.date))

    // Metrics
    const totalHarvested = harvests.reduce((sum, h) => sum + h.quantity, 0)
    const qualityBreakdown = {
        Premium: harvests.filter(h => h.quality === 'Premium').length,
        Primera: harvests.filter(h => h.quality === 'Primera').length,
        Segunda: harvests.filter(h => h.quality === 'Segunda').length,
        Tercera: harvests.filter(h => h.quality === 'Tercera').length
    }

    return {
        harvests,
        labors,
        metrics: {
            totalHarvested: Math.round(totalHarvested),
            avgPerDay: Math.round(totalHarvested / days),
            kgPerPlant: randomInRange(1.5, 4.5, 2),
            kgPerM2: randomInRange(3, 12, 2),
            tonPerHa: randomInRange(config.finance.yieldTonHa * 0.8, config.finance.yieldTonHa * 1.1, 2)
        },
        qualityBreakdown
    }
}

// ========================================
// FINANCIAL DATA (Previous month)
// ========================================
export function generateFinanceData(crop) {
    const config = CROP_CONFIG[crop] || CROP_CONFIG['Tomate']
    const { pricePerKg, yieldTonHa, productionCostPerHa } = config.finance

    // Calculate previous month dates
    const today = new Date()
    const prevMonthStart = new Date(today.getFullYear(), today.getMonth() - 1, 1)
    const prevMonthEnd = new Date(today.getFullYear(), today.getMonth(), 0)
    const daysInMonth = prevMonthEnd.getDate()

    // Generate daily sales for previous month
    const sales = []
    let totalSalesKg = 0
    let totalSalesValue = 0

    const clients = ['Corabastos', 'Supermercado Éxito', 'Mercado Local', 'Exportación', 'Distribuidor Regional']

    for (let day = 1; day <= daysInMonth; day++) {
        const date = new Date(prevMonthStart.getFullYear(), prevMonthStart.getMonth(), day)
        const hasSale = Math.random() > 0.3

        if (hasSale) {
            const dailyKg = randomInRange(50, 500, 1)
            const priceVariation = randomInRange(0.9, 1.1, 2)
            const actualPrice = Math.round(pricePerKg * priceVariation)
            const total = dailyKg * actualPrice

            totalSalesKg += dailyKg
            totalSalesValue += total

            sales.push({
                id: sales.length + 1,
                date: formatDate(date),
                dateDisplay: date.toLocaleDateString('es-CO', { day: '2-digit', month: 'short' }),
                client: clients[Math.floor(Math.random() * clients.length)],
                product: crop,
                quantityKg: dailyKg,
                pricePerKg: actualPrice,
                total
            })
        }
    }

    // Generate costs by category
    const costCategories = [
        { name: 'Mano de Obra', percentage: 0.35 },
        { name: 'Insumos Agrícolas', percentage: 0.25 },
        { name: 'Transporte', percentage: 0.10 },
        { name: 'Servicios Públicos', percentage: 0.08 },
        { name: 'Arriendo', percentage: 0.12 },
        { name: 'Mantenimiento', percentage: 0.05 },
        { name: 'Otros', percentage: 0.05 }
    ]

    const monthlyBudget = productionCostPerHa / 12
    const costs = []
    let totalCosts = 0

    costCategories.forEach((cat, index) => {
        const amount = Math.round(monthlyBudget * cat.percentage * randomInRange(0.9, 1.1, 2))
        totalCosts += amount

        costs.push({
            id: index + 1,
            category: cat.name,
            amount,
            percentage: Math.round(cat.percentage * 100),
            type: index < 2 ? 'Variable' : 'Fijo'
        })
    })

    // Generate cash flow (last 6 months)
    const cashFlow = []
    for (let i = 5; i >= 0; i--) {
        const month = new Date(today.getFullYear(), today.getMonth() - i, 1)
        const monthIncome = totalSalesValue * randomInRange(0.8, 1.2, 2)
        const monthExpenses = totalCosts * randomInRange(0.85, 1.15, 2)

        cashFlow.push({
            month: month.toLocaleDateString('es-CO', { month: 'short', year: '2-digit' }),
            income: Math.round(monthIncome),
            expenses: Math.round(monthExpenses),
            profit: Math.round(monthIncome - monthExpenses)
        })
    }

    // KPIs
    const grossMargin = ((totalSalesValue - totalCosts) / totalSalesValue) * 100
    const roi = ((totalSalesValue - totalCosts) / totalCosts) * 100

    return {
        period: {
            start: formatDate(prevMonthStart),
            end: formatDate(prevMonthEnd),
            label: prevMonthStart.toLocaleDateString('es-CO', { month: 'long', year: 'numeric' })
        },
        sales,
        costs,
        cashFlow,
        summary: {
            totalIncome: Math.round(totalSalesValue),
            totalExpenses: Math.round(totalCosts),
            grossProfit: Math.round(totalSalesValue - totalCosts),
            grossMargin: grossMargin.toFixed(1),
            roi: roi.toFixed(1),
            totalKgSold: Math.round(totalSalesKg),
            avgPricePerKg: Math.round(totalSalesValue / totalSalesKg)
        },
        kpis: {
            breakEvenKg: Math.round(totalCosts / pricePerKg),
            profitPerKg: Math.round((totalSalesValue - totalCosts) / totalSalesKg),
            costPerKg: Math.round(totalCosts / totalSalesKg)
        }
    }
}

// ========================================
// COMPLETE DATA GENERATOR
// ========================================
export function generateAllData(crop) {
    return {
        crop,
        config: CROP_CONFIG[crop],
        soil: {
            current: generateSoilData(crop),
            history: generateSoilHistoryByZone(crop, 30)
        },
        environment: {
            current: generateEnvironmentData(crop),
            history: generateEnvironmentHistory(crop, 30)
        },
        productivity: generateProductivityData(crop, 30),
        finance: generateFinanceData(crop),
        alerts: generateAlerts(crop),
        generatedAt: new Date().toISOString()
    }
}

export default {
    generateSoilData,
    generateHistoricalData,
    generateSoilHistoryByZone,
    generateEnvironmentData,
    generateEnvironmentHistory,
    generateProductivityData,
    generateFinanceData,
    generateAlerts,
    generateAllData,
    CROP_CONFIG,
    CROP_RANGES
}
